// src/Components/Forms/AddProjectModal.js
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    Box,
    useTheme,
    useMediaQuery,
    alpha,
    Backdrop,
    CircularProgress
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import ProjectForm from './ProjectForm';
import { api } from '../../config';
import {
    toastSuccess,
    toastError,
    toastInfo,
    toastLoading,
    updateToast
} from '../Common/toastHelper';

const themeColors = {
    primary: '#99191b',
    secondary: '#e3764c'
};

const AddProjectModal = ({ isOpen, onClose, onProjectAdded }) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const [formData, setFormData] = useState({
        titolo: '',
        sottotitolo: '',
        committente: '',
        incarico: '',
        citta: '',
        inizio: '',
        fine: '',
        descrizione: '',
        categorie: [],       // ⬅️ multi-categoria (come ProjectForm)
        is_important: false,
        images: []
    });

    const [loading, setLoading] = useState(false);

    const resetForm = () => setFormData({
        titolo: '',
        committente: '',
        incarico: '',
        citta: '',
        inizio: '',
        fine: '',
        descrizione: '',
        categorie: [],
        is_important: false,
        images: []
    });

    const handleClose = () => {
        resetForm();
        onClose();
        toastInfo('Aggiunta progetto annullata');
    };

    // Gestione immagini
    const handleImagesChange = (event) => {
        const files = Array.from(event.target.files || []);

        const valid = files.filter((file) => {
            if (!file.type.startsWith('image/')) {
                toastError(`${file.name} non è un'immagine valida`);
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                toastError(`${file.name} supera 5MB`);
                return false;
            }
            return true;
        });

        const newImages = valid.map((file, index) => ({
            id: `new-${Date.now()}-${index}`,
            file,
            preview: URL.createObjectURL(file),
            path: file.name,
            isHero: formData.images.length === 0 && index === 0 // prima immagine = hero
        }));

        setFormData((prev) => ({ ...prev, images: [...prev.images, ...newImages] }));
        if (valid.length > 0) {
            toastSuccess(`${valid.length} immagini caricate con successo`);
        }
    };

    const removeImage = (index) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.filter((_, i) => i !== index)
        }));
        toastInfo('Immagine rimossa');
    };

    const setHeroImage = (index) => {
        setFormData((prev) => ({
            ...prev,
            images: prev.images.map((img, i) => ({ ...img, isHero: i === index }))
        }));
        toastInfo('Immagine impostata come copertina');
    };

    // Validazione coerente con ProjectForm (usa categorie[])
    const validate = (data = formData) => {
        const {
            titolo,
            committente,
            incarico,
            citta,
            inizio,
            fine,
            images,
            categorie
        } = data;

        console.log('Dati in validazione (AddProject):', {
            titolo,
            committente,
            incarico,
            citta,
            inizio,
            fine,
            categorie,
            imagesCount: images.length,
            hasHero: images.some((i) => i.isHero)
        });

        if (!titolo?.trim()) {
            toastError('Il titolo è obbligatorio');
            return false;
        }
        if (!committente?.trim()) {
            toastError('Il committente è obbligatorio');
            return false;
        }
        if (committente.trim().length > 255) {
            toastError('Il committente non può superare 255 caratteri');
            return false;
        }
        if (!incarico?.trim()) {
            toastError("L'incarico è obbligatorio");
            return false;
        }
        if (!citta?.trim()) {
            toastError('La città è obbligatoria');
            return false;
        }

        // categorie[] obbligatorie
        if (!Array.isArray(categorie) || categorie.length === 0) {
            toastError('La categoria è obbligatoria');
            return false;
        }

        const yearPattern = /^\d{4}$/;
        if (!inizio || !yearPattern.test(inizio)) {
            toastError("L'anno di inizio deve essere di 4 cifre (es. 2023)");
            return false;
        }
        if (!fine || !yearPattern.test(fine)) {
            toastError("L'anno di fine deve essere di 4 cifre (es. 2024)");
            return false;
        }

        if (!images.length) {
            toastError("Carica almeno un'immagine (copertina)");
            return false;
        }
        if (!images.some((i) => i.isHero)) {
            toastError("Seleziona un'immagine di copertina");
            return false;
        }

        return true;
    };

    const handleSubmit = async (submissionData = null) => {
        const dataToSubmit = submissionData || formData;

        if (!validate(dataToSubmit)) return;

        setLoading(true);
        const loadingToast = toastLoading('Creazione progetto in corso...');

        try {
            const fd = new FormData();
            fd.append('titolo', dataToSubmit.titolo);
            fd.append('sottotitolo', dataToSubmit.sottotitolo || '');
            fd.append('committente', dataToSubmit.committente);
            fd.append('incarico', dataToSubmit.incarico);
            fd.append('citta', dataToSubmit.citta);
            fd.append('inizio', dataToSubmit.inizio);
            fd.append('fine', dataToSubmit.fine);
            if (dataToSubmit.descrizione !== undefined) {
                fd.append('descrizione', dataToSubmit.descrizione || '');
            }

            // categorie[] → backend M2M
            (dataToSubmit.categorie || []).forEach((cid) => {
                fd.append('categorie[]', String(cid));
            });

            fd.append(
                'is_important',
                dataToSubmit.is_important ? '1' : '0'
            );

            // immagini: 'immagine1' per la hero, 'image_{n}' per le altre
            let extraIdx = 1;
            dataToSubmit.images.forEach((img) => {
                if (!img.file) return;
                if (img.isHero) {
                    fd.append('immagine1', img.file);
                } else {
                    fd.append(`image_${extraIdx}`, img.file);
                    extraIdx += 1;
                }
            });

            const response = await fetch(
                api('/addProject.php'),
                {
                    method: 'POST',
                    body: fd
                }
            );

            const text = await response.text();
            let result;
            try {
                result = JSON.parse(text);
            } catch (e) {
                console.error('Risposta non valida da addProject.php:', text);
                throw new Error('Risposta non valida dal server');
            }

            if (result.success) {
                updateToast(
                    loadingToast,
                    result.message ||
                    'Progetto creato con successo!',
                    'success'
                );
                handleClose();
                onProjectAdded && onProjectAdded();
            } else {
                throw new Error(
                    result.error || 'Errore nella creazione del progetto'
                );
            }
        } catch (err) {
            console.error(err);
            updateToast(
                loadingToast,
                `Errore durante la creazione: ${err.message}`,
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            toastInfo(
                'Modal aggiunta progetto aperto - Compila tutti i campi richiesti'
            );
        }
    }, [isOpen]);

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            fullScreen={fullScreen}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: fullScreen ? 0 : 3,
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    background: `linear-gradient(90deg, ${themeColors.primary} 0%, ${themeColors.secondary} 100%)`,
                    color: 'white',
                    py: 2,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <AddIcon />
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{ fontWeight: 600 }}
                    >
                        Aggiungi Nuovo Progetto
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: 'white',
                        '&:hover': {
                            backgroundColor: alpha('#fff', 0.1)
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, position: 'relative' }}>
                {loading && (
                    <Backdrop
                        open
                        sx={{
                            color: '#fff',
                            zIndex: (t) => t.zIndex.drawer + 1,
                            position: 'absolute',
                            backgroundColor: 'rgba(0,0,0,0.55)'
                        }}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                )}
                <ProjectForm
                    formData={formData}
                    setFormData={setFormData}
                    handleImagesChange={handleImagesChange}
                    removeImage={removeImage}
                    setHeroImage={setHeroImage}
                    handleSubmit={handleSubmit}
                    loading={loading}
                    onCancel={handleClose}
                    projectId={null}
                    isEdit={false}
                />
            </DialogContent>
        </Dialog>
    );
};

export default AddProjectModal;
